import React, { useEffect, useState } from 'react';
import { FlatList, Image, ActivityIndicator } from "react-native"
import { Switch, Toggle } from '../../Components';
import DropDown from '../../Components/Dropdown';
import type { EventProps, LeagueProps, MarketProps, TournamentProps, TradeProps } from '../../types';
import { ManageCompetitionApi, ManageCompetitionMarketApi, ManageCompetitionMatchApi } from '../api';
import moment from 'moment-mini';
import SearchBox from '../../Components/SearchBox';
import { useColors } from '../../constants/useColors';
import { Button, Text, View } from '../../Components/Themed';
import { MarketComponentHelpers } from '../../MarketComponents/api';

type ContestSelectorProps = {
    action_loading:boolean,
    leagues:LeagueProps[],
    active_markets?:string[],
    selected_team_events:EventProps[],
    selected_tournaments?:TournamentProps[],
    onSelectTournament?:(tournament:TournamentProps) => void,
    onDeSelectTournament?:(tournament:TournamentProps) => void,
    onSelectTeamEvent:(event:EventProps, trades:TradeProps[]) => void,
    onSelectTeamEvents?: (events:EventProps[], trades:TradeProps[]) => void,
    onDeselectTeamEvent: (event:EventProps) => void,
    onClose:() => void
}

const ContestSelector = ({ action_loading, leagues, selected_team_events, selected_tournaments, onClose, onSelectTournament, onDeSelectTournament, onSelectTeamEvent, onDeselectTeamEvent, onSelectTeamEvents }:ContestSelectorProps) => {
    const Colors = useColors();
    const [ search_value, setSearchValue ] = useState('');
    const [ filters, setFilters ] = useState<{
        ranked?:boolean,
        division?:string,
        sub_division?:string
    }>({})
    const [ selector_data, setData ] = useState<{
        loading:boolean,
        events:EventProps[],
        active_toggle:'events'|'tournaments',
        tournaments:TournamentProps[],
        markets:MarketProps[],
        trades:TradeProps[],
        range_dates:string[],
        active_league?:LeagueProps,
        active_date: string
    }>({
        loading:false,
        markets:[],
        active_toggle:'events',
        events: [],
        tournaments:[],
        trades:[],
        range_dates: [],
        active_date: moment().format('YYYY/MM/DD')
    })

    const { loading, events, range_dates, tournaments, trades, markets, active_league, active_date } = selector_data;

    let filtered_events = events.filter(e => `${e.event_id} ${e.away?.market_name} ${e.away?.name} ${e.home?.market_name} ${e.home?.name}`.toLowerCase().includes(search_value.toLowerCase()))
    .filter(e => moment(e.scheduled_datetime).format('YYYY/MM/DD') == active_date)
    .sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix());

    const divisions = [ ...new Set(filtered_events.map(e => e.away?.division ?? '').concat(filtered_events.map(e => e.home?.division ?? ''))) ].sort((a,b) => {
        return (a < b) ? -1 : (a > b) ? 1 : 0;
    })
    const show_ranked = filtered_events.find(e => e.away?.rank || e.home?.rank) ? true : false


    if(filters.division){
        filtered_events = filtered_events.filter(e => `${e.away?.division}` == filters.division || `${e.home?.division}` == filters.division)
    }
    const sub_divisions = [ ...new Set(filtered_events.filter(e => e.away?.division == filters.division || e.home?.division == filters.division).map(e => e.away?.sub_division ?? '').concat(filtered_events.map(e => e.home?.sub_division ?? ''))) ].sort((a,b) => {
        return (a < b) ? -1 : (a > b) ? 1 : 0;
    })

    if(filters.sub_division){
        filtered_events = filtered_events.filter(e => `${e.away?.sub_division}` == filters.sub_division || `${e.home?.sub_division}` == filters.sub_division)
    }
    if(filters.ranked){
        filtered_events = filtered_events.filter(e => e.away?.rank || e.home?.rank)
    }

    const show_add_all = filtered_events.filter(e => !selected_team_events.map(e => e.event_id.toString()).includes(e.event_id)).length > 0 ? true : false

    const spread_market = markets.find(mk => mk.type == 'Spread');

    useEffect(() => {
        if(leagues.length == 0){ return }
        if(!active_league){ setData({ ...selector_data, active_league: leagues.sort((a,b) => a.priority - b.priority)[0] }) }
    },[leagues])

    useEffect(() => {
        if(!active_league){ return }
        getDataFromServer(active_league.league_id)
    },[active_league])

    const getDataFromServer = async(league_id:string) => {
        setData({ ...selector_data, loading:true });
        const tourneys = await ManageCompetitionMatchApi.getTournamentsByLeagueId(league_id, 0, 'scheduled');
        const resp = await ManageCompetitionMatchApi.getEventsByDate(moment().format('YYYY/MM/DD'), league_id);
        const unique_dates = [ ...new Set(resp.range_dates.filter(d => moment(d).isAfter(moment().subtract(1, 'days'))).sort((a,b) => moment(a).unix() - moment(b).unix()).map(d => moment(d).format('YYYY/MM/DD'))) ]
        //Check to see if we have an event on today's date
        let best_day = unique_dates.find(d => d == moment().format('YYYY/MM/DD'))
        if(!best_day){
            best_day = unique_dates[0] ?? moment().format('YYYY/MM/DD')
        }
        
        //Lets grab all the last trades for events that we have
        let ev_markets = await ManageCompetitionApi.getMarkets();
        let event_trades = await ManageCompetitionMarketApi.getLatestTradesByEventIds(resp.events.map(e => e.event_id), 'team');


        setData({ ...selector_data, loading:false, tournaments:tourneys, events: resp.events, range_dates: unique_dates, active_date: best_day, trades: event_trades.filter(t => t.market_type == 'FOR_MONEY'), markets:ev_markets })
    }

    const renderDivisions = (data: { item:string, index:number }) => {
        const selected = filters.division == data.item ? true : false
        return (
            <Button style={{ padding:10 }} onPress={() => selected ? setFilters({ ...filters, division: undefined }) : setFilters({ ...filters, division:data.item })}>
                <Text theme={selected?'h1':'description'}>{data.item}</Text>
            </Button>
        )
    }
    const renderSubDivisions = (data: { item:string, index:number }) => {
        const selected = filters.sub_division == data.item ? true : false
        return (
            <Button style={{ padding:10 }} onPress={() => selected ? setFilters({ ...filters, sub_division: undefined }) : setFilters({ ...filters, sub_division:data.item })}>
                <Text theme={selected?'h1':'description'}>{data.item}</Text>
            </Button>
        )
    }

    const renderDates = (data: { item: string, index:number }) => {
        const formatted_date = moment(data.item, 'YYYY/MM/DD')
        const active = active_date == data.item ? true : false
        return (
            <Button style={{ padding:10, justifyContent:'center', alignItems:'center' }} onPress={() => setData({ ...selector_data, active_date: data.item })}>
                <Text size={12} theme={active?'h1':'description'} textAlign='center' >{formatted_date.format('ddd')}</Text>
                <Text style={{ marginTop:3 }} size={12} color={Colors.text.h1} textAlign='center' theme={active?'h1':'description'}>{formatted_date.format('MMM DD')}</Text>
            </Button>
        )
    }

    const renderEvents = (data: { item:EventProps, index:number }) => {
        const selected = selected_team_events.map(e => e.event_id.toString()).includes(data.item.event_id.toString()) ? true : false
        const is_live = moment(data.item.scheduled_datetime).isBefore(moment()) ? true : false
        const event_trades = trades.filter(t => t.event_type == 'team' && t.event_id == data.item.event_id && t.market_type == 'FOR_MONEY')
        const spread_trades = event_trades.filter(t => t.market_id == spread_market?.market_id);
        const home_trade = spread_trades.find(t => t.side_id == data.item.home_team_id)
        return (
            <View type='row' style={{  padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View style={{ flexDirection:'row', alignItems:'center', paddingRight:10, borderRightWidth:1, borderColor:Colors.borders.light }}>
                    <Image
                        source={{ uri: data.item.away?.image?.url }}
                        style={{ height:34, width:34, borderRadius:4 }}
                        resizeMode='cover'
                    />
                    <Text style={{ padding:5 }} theme='description'>VS</Text>
                    <Image
                        source={{ uri: data.item.home?.image?.url }}
                        style={{ height:34, width:34, borderRadius:4 }}
                        resizeMode='cover'
                    />
                </View>
                <View style={{ flex:1, marginLeft: 10 }}>
                    <Text theme='h2'>{data.item.event_title}{home_trade && spread_market?` ${MarketComponentHelpers.getVar1Label(spread_market, home_trade.var_1)}`:''}</Text>
                    <Text style={{ marginTop:3 }} theme='description'>{data.item.time_detail == 'scheduled' ? moment(data.item.scheduled_datetime).format('MMM DD hh:mm a'): data.item.time_detail}</Text>
                </View>
                {!is_live ?
                <Switch
                    switch_type='on_off'
                    value={selected}
                    onChange={() => {
                        if(action_loading){ return }
                        if(selected){ return onDeselectTeamEvent(data.item) } 
                        return onSelectTeamEvent(data.item, event_trades)
                    }}
                />
                :<></>}
            </View>
        )
    }

    const renderTournaments = (data:{ item:TournamentProps, index:number }) => {
        const selected = selected_tournaments ? selected_tournaments.map(e => e.tournament_id.toString()).includes(data.item.tournament_id.toString()) : false
        const league = leagues.find(l => l.league_id == data.item.league_id);
        return (
            <View type='row' style={{  padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <Image
                    source={{ uri: league?.league_image }}
                    style={{ height:34, width:34, borderRadius:4 }}
                    resizeMode='cover'
                />                    
                <View style={{ flex:1, marginLeft: 10 }}>
                    <Text theme='h2'>{data.item.tournament_name}</Text>
                    <Text style={{ marginTop:3 }} theme='description'>{data.item.time_detail == 'scheduled' ? moment(data.item.scheduled_datetime).format('MMM DD hh:mm a'): data.item.time_detail}</Text>
                </View>
                <Switch
                    switch_type='on_off'
                    value={selected}
                    onChange={() => {
                        if(action_loading){ return }
                        if(!onSelectTournament){ return }
                        if(!onDeSelectTournament){ return }
                        if(selected){ return onDeSelectTournament(data.item) } 
                        return onSelectTournament(data.item)
                    }}
                />
            </View>
        )
    }

    return (
        <View float style={{ flex:1 }}>
            <View type='header' style={{ flexDirection:'row', zIndex:10, alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                {active_league ?
                <Image
                    source={{ uri: active_league.league_image }}
                    style={{ height:50, width:50, marginRight:10, borderRadius:4 }}
                    resizeMode='cover'
                />
                :<></>}
                <View transparent style={{ flex:1, marginRight:50 }}>
                    <Text theme='h1'>MANAGE CONTESTS</Text>
                    <Text style={{ marginTop:3 }} theme='description'>Add / Remove contests from the competition</Text>
                </View>
                <View style={{ position: 'absolute', top:10, right:10, justifyContent:'center', alignItems:'center' }}>
                    <DropDown
                        dropdown_options={[
                            {value:'league_id', eligible_options: leagues.sort((a,b) => a.priority - b.priority).map(l => l.league_name)}
                        ]}
                        selected_value={active_league?.league_name ?? 'Select League'}
                        onOptionSelect={(league_name) => { 
                            const league = leagues.find(l => l.league_name == league_name);
                            setData({ ...selector_data, active_league: league })
                        }}
                    />
                </View>
            </View>
            <View style={{ flex:1 }}>
                {onSelectTournament ?
                <View transparent style={{ padding:10 }}>
                    <Toggle
                        options={[
                            { key: 'events', label: 'Events' },
                            { key: 'tournaments', label: 'Tournaments' }
                        ]}
                        onSelectOption={(toggle) => setData({ ...selector_data, active_toggle: toggle as 'events'|'tournaments' })}
                        selected_option={selector_data.active_toggle}
                    />
                </View>
                :<></>}
                {selector_data.active_toggle == 'events' ?
                <View style={{ flex:1 }}>
                    <View style={{ padding:10 }}>
                    <FlatList
                        data={range_dates}
                        renderItem={renderDates}
                        horizontal
                        keyExtractor={(item) => item}
                    />
                    </View>
                    <View style={{ marginTop:0 }}>
                        <SearchBox 
                            onChange={(text) => setSearchValue(text)}
                            hide_search_button
                        />
                    </View>
                    {divisions.length > 1 ?
                    <View nativeID='filters' style={{ flexDirection:'row' }}>
                        {show_ranked ?
                        <Button style={{ padding:10 }} onPress={() => filters.ranked ? setFilters({ ...filters, ranked: false }) : setFilters({ ...filters, ranked: true })}>
                            <Text theme={filters.ranked ? 'h1':'description'}>Top 25</Text>
                        </Button>
                        :<></>}
                        <FlatList
                            data={divisions}
                            renderItem={renderDivisions}
                            horizontal
                            keyExtractor={(item) => item}
                        />
                    </View>
                    :<></>}
                    {filters.division && sub_divisions.length > 1 ?
                    <View>
                        <FlatList
                            data={sub_divisions}
                            renderItem={renderSubDivisions}
                            horizontal
                            keyExtractor={(item) => item}
                        />
                    </View>
                    :<></>}
                    <View style={{ flex:1, padding:10 }}>
                        {loading ?
                        <ActivityIndicator style={{ padding:20, alignSelf:'center' }} size='large' color={Colors.text.h1} />
                        :<></>}
                        <FlatList
                            data={filtered_events}
                            renderItem={renderEvents}
                            keyExtractor={(item) => item.event_id.toString()}
                        />
                    </View>
                </View>
                :
                <View style={{ flex:1 }}>
                    <FlatList
                        data={tournaments}
                        renderItem={renderTournaments}
                        key={'tourney_list'}
                        keyExtractor={(item) => item.tournament_id.toString()}
                    />
                </View>
                }
            </View>
            <View type='footer' style={{ flexDirection:'row', padding:10 }}>
                {onSelectTeamEvents && filtered_events.length > 0 ?
                <Button
                    title='ADD ALL'
                    style={{ flex:2, marginRight:10, opacity:!show_add_all || action_loading?0.5:1 }}
                    loading={action_loading}
                    disabled={!show_add_all || action_loading}
                    type='success'
                    onPress={() => {
                        ///First remove any event that is already selected
                        let add_events = filtered_events.filter(e => !selected_team_events.map(e => e.event_id.toString()).includes(e.event_id))
                        onSelectTeamEvents(add_events, trades);
                    }}
                />
                :<></>}
                <Button
                    title='CLOSE'
                    style={{ flex:1 }}
                    type='error'
                    onPress={() => onClose()}
                />
            </View>
        </View>
    )
}

export default ContestSelector